import { createHostTransport } from './transport.ts';

const text = (value) => String(value ?? '').trim();
const VALIDATION_METHODS = new Set(['validateSend', 'validateOperation']);

/** Host routing metadata only. Each invocation captures a fixed transport;
 * Session ownership comes from canonical records or its scoped creation/list read.
 * Binding a view never owns an AbortController or changes a backend Run.
 */
export const createSessionScopeTransport = (options, configuration) => {
  const bindings = new Map();
  const sessions = new Map();
  const binding = (projectId) => {
    const key = text(projectId);
    if (!bindings.has(key)) bindings.set(key, createHostTransport(options, {
      ...configuration,
      scope: { ...configuration.scope, projectId: key },
      sessionDefaults: { ...configuration.sessionDefaults, project_id: key },
      sourceDefaults: { ...configuration.sourceDefaults, project_id: key },
    }));
    return bindings.get(key);
  };
  const base = binding(configuration.scope?.projectId);
  let current = base;
  const remember = (record, admitted) => {
    const id = text(record?.sessionId);
    const projectId = text(record?.project_id);
    const owner = projectId ? binding(projectId) : admitted;
    if (id && owner) sessions.set(id, owner);
    return record;
  };
  const forSession = async (id, request = {}) => {
    request.signal?.throwIfAborted();
    if (!sessions.has(id)) remember(await base.sessions.get(id, request));
    request.signal?.throwIfAborted();
    if (!sessions.has(id)) throw new TypeError('Session project scope must be authoritative');
    return sessions.get(id);
  };
  const sessionPort = {
    ...base.sessions,
    list: async (input, request) => {
      const owner = current;
      const result = await owner.sessions.list(input, request);
      return { ...result, items: result.items.map((record) => remember(record, owner)) };
    },
    create: async (input, request) => {
      const owner = current;
      return remember(await owner.sessions.create(input, request), owner);
    },
    get: async (id, request) => remember(await base.sessions.get(id, request), sessions.get(id)),
  };
  const sourcePort = Object.fromEntries(Object.keys(base.sources).map((method) => [method,
    async (id, ...args) => {
      const owner = await forSession(id, args.at(-1));
      return owner.sources[method](id, ...args);
    },
  ]));
  const streamPort = Object.fromEntries(Object.entries(base.stream).map(([method, execute]) => [method,
    VALIDATION_METHODS.has(method) ? execute : async (input, onEvent, request) => {
      const owner = await forSession(input.sessionId, request);
      return owner.stream[method](input, onEvent, request);
    },
  ]));
  return {
    bindScope: (projectId) => { current = binding(projectId); },
    transport: { ...base, sessions: sessionPort, sources: sourcePort, stream: streamPort },
  };
};
